import { findDrivingChallengeById, type DrivingChallengeId } from '../data/challengeOptions';
import { findDrivingCameraById } from '../data/drivingCameraOptions';
import { findDrivingCourseById } from '../data/courseOptions';
import { findCarModelById } from '../data/carModels';
import { findTimeOfDayById } from '../data/timeOfDayOptions';
import {
  createDefaultDrivingPreferences,
  isRegenMode,
  type DrivingPreferences,
} from './drivingPreferences';
import { isChallengeLowerBetter } from './drivingChallenges';
import {
  isValidTuningValue,
  type VehicleTuning,
  type VehicleTuningField,
} from './vehicleTuning';

export const DRIVING_PREFERENCES_STORAGE_KEY = 'towncar-showroom:driving-preferences:v1';
export const DRIVING_RECORDS_STORAGE_KEY = 'towncar-showroom:driving-records:v1';
const STORAGE_VERSION = 1;
const RECORD_SEPARATOR = '::';

interface StoredEnvelope {
  version: number;
  data: unknown;
}

export interface DrivingRecordBook {
  /** `${vehicleId}::${courseId}` → 초. */
  lapBestByKey: Record<string, number>;
  /** `${vehicleId}::${challengeId}` → 챌린지 단위. */
  challengeBestByKey: Record<string, number>;
}

export function createEmptyDrivingRecordBook(): DrivingRecordBook {
  return { lapBestByKey: {}, challengeBestByKey: {} };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isEnvelope(value: unknown): value is StoredEnvelope {
  return isObject(value) && value.version === STORAGE_VERSION && 'data' in value;
}

function parseStoredValue(key: string): unknown {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? undefined : JSON.parse(raw);
  } catch {
    // 보안 설정·용량·손상된 JSON이 현재 세션의 주행을 막으면 안 됩니다.
    return undefined;
  }
}

function writeStoredValue(key: string, data: unknown): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(key, JSON.stringify({ version: STORAGE_VERSION, data }));
  } catch {
    // private browsing이나 저장소 차단 환경에서도 메모리 상태는 정상 동작합니다.
  }
}

function stringIf(value: unknown, predicate: (candidate: string) => boolean): string | undefined {
  return typeof value === 'string' && predicate(value) ? value : undefined;
}

function decodeTuning(value: unknown, fallback: VehicleTuning): VehicleTuning {
  if (!isObject(value)) {
    return { ...fallback };
  }
  const next = { ...fallback };
  (Object.keys(next) as VehicleTuningField[]).forEach((field) => {
    const candidate = value[field];
    if (typeof candidate === 'string' && isValidTuningValue(field, candidate)) {
      // isValidTuningValue가 field별 union을 보장하지만 TS는 상관관계를 보존하지 못합니다.
      (next as Record<VehicleTuningField, string>)[field] = candidate;
    }
  });
  return next;
}

/**
 * 외부 저장값을 현재 카탈로그에 맞는 완전한 설정으로 정규화합니다.
 * 옛 버전의 일부 필드가 빠져도 나머지 유효한 선택은 보존합니다.
 */
export function decodeDrivingPreferences(value: unknown): DrivingPreferences {
  const fallback = createDefaultDrivingPreferences();
  if (!isObject(value)) {
    return fallback;
  }

  const challengeId = stringIf(value.challengeId, (id) => findDrivingChallengeById(id) !== undefined)
    ?? fallback.challengeId;
  const challenge = findDrivingChallengeById(challengeId)!;
  const storedCourseId = stringIf(value.courseId, (id) => findDrivingCourseById(id) !== undefined)
    ?? fallback.courseId;
  // 제한 챌린지를 저장한 경우 첫 렌더부터 실제 측정 코스로 맞춥니다.
  const courseId = challenge.requiredCourseId ?? storedCourseId;

  return {
    courseId,
    timeOfDayId:
      stringIf(value.timeOfDayId, (id) => findTimeOfDayById(id) !== undefined)
      ?? fallback.timeOfDayId,
    cameraMode:
      (stringIf(value.cameraMode, (id) => findDrivingCameraById(id) !== undefined)
        ?? fallback.cameraMode) as DrivingPreferences['cameraMode'],
    weatherMode:
      value.weatherMode === 'fixed' || value.weatherMode === 'dynamic'
        ? value.weatherMode
        : fallback.weatherMode,
    challengeId: challengeId as DrivingChallengeId,
    regenMode: isRegenMode(value.regenMode) ? value.regenMode : fallback.regenMode,
    assists: {
      abs: typeof value.assists === 'object' && value.assists !== null
        && typeof (value.assists as Record<string, unknown>).abs === 'boolean'
        ? (value.assists as Record<string, boolean>).abs
        : fallback.assists.abs,
      tcs: typeof value.assists === 'object' && value.assists !== null
        && typeof (value.assists as Record<string, unknown>).tcs === 'boolean'
        ? (value.assists as Record<string, boolean>).tcs
        : fallback.assists.tcs,
      esc: typeof value.assists === 'object' && value.assists !== null
        && typeof (value.assists as Record<string, unknown>).esc === 'boolean'
        ? (value.assists as Record<string, boolean>).esc
        : fallback.assists.esc,
    },
    tuning: decodeTuning(value.tuning, fallback.tuning),
  };
}

export function readDrivingPreferences(): DrivingPreferences {
  const stored = parseStoredValue(DRIVING_PREFERENCES_STORAGE_KEY);
  return isEnvelope(stored) ? decodeDrivingPreferences(stored.data) : createDefaultDrivingPreferences();
}

export function writeDrivingPreferences(preferences: DrivingPreferences): void {
  writeStoredValue(DRIVING_PREFERENCES_STORAGE_KEY, preferences);
}

function recordKey(vehicleId: string, activityId: string): string {
  return `${vehicleId}${RECORD_SEPARATOR}${activityId}`;
}

function decodeRecordMap(
  value: unknown,
  activityExists: (id: string) => boolean
): Record<string, number> {
  if (!isObject(value)) {
    return {};
  }
  const decoded: Record<string, number> = {};
  Object.entries(value).forEach(([key, result]) => {
    const [vehicleId, activityId, extra] = key.split(RECORD_SEPARATOR);
    if (
      extra === undefined
      && vehicleId
      && activityId
      && findCarModelById(vehicleId) !== undefined
      && activityExists(activityId)
      && typeof result === 'number'
      && Number.isFinite(result)
      && result > 0
      && result < 1_000_000
    ) {
      decoded[key] = result;
    }
  });
  return decoded;
}

export function decodeDrivingRecordBook(value: unknown): DrivingRecordBook {
  if (!isObject(value)) {
    return createEmptyDrivingRecordBook();
  }
  return {
    lapBestByKey: decodeRecordMap(
      value.lapBestByKey,
      (courseId) => findDrivingCourseById(courseId) !== undefined
    ),
    challengeBestByKey: decodeRecordMap(
      value.challengeBestByKey,
      (challengeId) =>
        challengeId !== 'free-drive' && findDrivingChallengeById(challengeId) !== undefined
    ),
  };
}

export function readDrivingRecordBook(): DrivingRecordBook {
  const stored = parseStoredValue(DRIVING_RECORDS_STORAGE_KEY);
  return isEnvelope(stored)
    ? decodeDrivingRecordBook(stored.data)
    : createEmptyDrivingRecordBook();
}

export function writeDrivingRecordBook(book: DrivingRecordBook): void {
  writeStoredValue(DRIVING_RECORDS_STORAGE_KEY, book);
}

export function getStoredLapBest(
  book: DrivingRecordBook,
  vehicleId: string,
  courseId: string
): number | null {
  return book.lapBestByKey[recordKey(vehicleId, courseId)] ?? null;
}

export function getStoredChallengeBest(
  book: DrivingRecordBook,
  vehicleId: string,
  challengeId: DrivingChallengeId
): number | null {
  return book.challengeBestByKey[recordKey(vehicleId, challengeId)] ?? null;
}

export function withLapResult(
  book: DrivingRecordBook,
  vehicleId: string,
  courseId: string,
  resultSeconds: number
): DrivingRecordBook {
  if (!Number.isFinite(resultSeconds) || resultSeconds <= 0) {
    return book;
  }
  const key = recordKey(vehicleId, courseId);
  const current = book.lapBestByKey[key];
  if (current !== undefined && current <= resultSeconds) {
    return book;
  }
  return { ...book, lapBestByKey: { ...book.lapBestByKey, [key]: resultSeconds } };
}

export function withChallengeResult(
  book: DrivingRecordBook,
  vehicleId: string,
  challengeId: DrivingChallengeId,
  result: number
): DrivingRecordBook {
  if (challengeId === 'free-drive' || !Number.isFinite(result) || result <= 0) {
    return book;
  }
  const key = recordKey(vehicleId, challengeId);
  const current = book.challengeBestByKey[key];
  const better = current === undefined
    || (isChallengeLowerBetter(challengeId) ? result < current : result > current);
  if (!better) {
    return book;
  }
  return { ...book, challengeBestByKey: { ...book.challengeBestByKey, [key]: result } };
}
